import type { SearchResponse, FirmaResponse, VorlesungstermineResponse, FirmaTermineHeuteResponse, NotenResponse, NotenHighlightsResponse, RechnungenResponse, ZahlungsstatusResponse, TnPersonKomplett, LoginResponse, AuthMeResponse, DublettenErgebnis, TeilnehmerAnlegenPayload, AnmeldungListResponse, EmployerAnmeldungDetail, FirmaMe, FirmaUserSummary, FirmaRole, AccessLevel } from '@/types'
import type {
  UserFlat as UserFlatApi,
  AuditFilters as AuditFiltersApi,
  AuditResponse as AuditResponseApi,
} from '@/pages/SuperAdmin/types'

const BASE = '/api'

class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url)
  if (!res.ok) {
    const body = await res.text().catch(() => res.statusText)
    throw new ApiError(res.status, `API-Fehler ${res.status}: ${body}`)
  }
  return res.json()
}

async function putJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    throw new ApiError(res.status, `API-Fehler ${res.status}: ${text}`)
  }
  return res.json()
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    throw new ApiError(res.status, `API-Fehler ${res.status}: ${text}`)
  }
  return res.json()
}

async function patchJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    throw new ApiError(res.status, `API-Fehler ${res.status}: ${text}`)
  }
  return res.json()
}

function buildAuditParams(filters: AuditFiltersApi): URLSearchParams {
  const map: Record<string, string> = {
    firmaId: 'firma_id',
    userId: 'user_id',
    action: 'action',
    from: 'from',
    to: 'to',
    q: 'q',
    page: 'page',
  }
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(filters)) {
    if (v == null || v === '') continue
    const key = map[k] ?? k
    p.set(key, String(v))
  }
  return p
}

async function deleteJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { method: 'DELETE' })
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    throw new ApiError(res.status, `API-Fehler ${res.status}: ${text}`)
  }
  return res.json()
}

export const api = {
  /** Suche nach Firmenname oder Accountnummer */
  search: (query: string) =>
    fetchJson<SearchResponse>(`${BASE}/suche?q=${encodeURIComponent(query)}`),

  /** Firmenstammdaten inkl. Ansprechpartner & Teilnehmer */
  firma: (accountId: string) =>
    fetchJson<FirmaResponse>(`${BASE}/firma/${accountId}`),

  /** Vorlesungstermine eines Teilnehmers */
  vorlesungstermine: (accountId: string, stnId: number) =>
    fetchJson<VorlesungstermineResponse>(`${BASE}/firma/${accountId}/teilnehmer/${stnId}/termine`),

  /** Termine aller Studierenden einer Firma für ein Datum */
  termineHeute: (accountId: string, datum?: string) => {
    const params = datum ? `?datum=${datum}` : ''
    return fetchJson<FirmaTermineHeuteResponse>(`${BASE}/firma/${accountId}/termine-heute${params}`)
  },

  /** Noten eines Teilnehmers für einen Studiengang */
  noten: (accountId: string, stnId: number) =>
    fetchJson<NotenResponse>(`${BASE}/firma/${accountId}/teilnehmer/${stnId}/noten`),

  /** Top- und Flop-Noten aller aktiven Teilnehmer einer Firma */
  notenHighlights: (accountId: string, limit = 5) =>
    fetchJson<NotenHighlightsResponse>(`${BASE}/firma/${accountId}/noten-highlights?limit=${limit}`),

  /** Rechnungen einer Firma */
  rechnungen: (firmenId: number) =>
    fetchJson<RechnungenResponse>(`${BASE}/rechnungen/${firmenId}`),

  /** Zahlungsstatus der Rechnungen einer Firma */
  zahlungsstatus: (firmenId: number) =>
    fetchJson<ZahlungsstatusResponse>(`${BASE}/rechnungen/${firmenId}/zahlungsstatus`),

  /** URL für PDF-Download einer Rechnung (direkt als link/iframe nutzbar) */
  rechnungPdfUrl: (rechnungsId: number) =>
    `${BASE}/rechnungen/${rechnungsId}/pdf`,

  /** Komplette Teilnehmerdaten aus TN-Schnittstelle */
  teilnehmerDetail: (accountId: string, personenId: number) =>
    fetchJson<TnPersonKomplett>(`${BASE}/firma/${accountId}/teilnehmer/${personenId}/detail`),

  /** URL für das Teilnehmer-Foto (direkt als img-src nutzbar) */
  teilnehmerPhotoUrl: (accountId: string, personenId: number) =>
    `${BASE}/firma/${accountId}/teilnehmer/${personenId}/photo`,

  /** URL für das FOM-Ansprechpartner-Foto (direkt als img-src nutzbar) */
  fomAnsprechpartnerPhotoUrl: (accountId: string, personenId: number) =>
    `${BASE}/firma/${accountId}/fom-ansprechpartner/${personenId}/photo`,

  /** URL für den PDF-Leistungsnachweis */
  leistungsnachweisUrl: (accountId: string, stnId: number) =>
    `${BASE}/firma/${accountId}/teilnehmer/${stnId}/leistungsnachweis`,

  /** Liste der Semesterpläne eines Teilnehmers (MyFomDocumentService) */
  semesterplaene: (accountId: string, stnId: number) =>
    fetchJson<{ semesterplaene: { dateiName: string; url: string }[]; count: number }>(
      `${BASE}/firma/${accountId}/teilnehmer/${stnId}/semesterplaene`,
    ),

  /** Liste der Prüfungsordnungs-Dokumente eines Teilnehmers (MyFomDocumentService poList) */
  pruefungsordnungen: (accountId: string, stnId: number) =>
    fetchJson<{ items: { dateiName: string; url: string; id?: number }[]; count: number }>(
      `${BASE}/firma/${accountId}/teilnehmer/${stnId}/pruefungsordnungen`,
    ),

  /** Direkt-URL zum PDF eines PO-Dokuments — für window.open / iframe-src */
  pruefungsordnungPdfUrl: (accountId: string, stnId: number, fileId: number) =>
    `${BASE}/firma/${accountId}/teilnehmer/${stnId}/po-pdf/${fileId}`,

  /** Direkt-URL zum PDF eines Semesterplan-Dokuments — für window.open */
  semesterplanPdfUrl: (accountId: string, stnId: number, fileId: number) =>
    `${BASE}/firma/${accountId}/teilnehmer/${stnId}/semesterplan-pdf/${fileId}`,

  // ── Updates (PUT – Partial) ──────────────────────────────

  /** Firmen-Stammdaten im CRM aktualisieren (Admin only) */
  updateStammdaten: (accountId: string, data: Record<string, unknown>) =>
    patchJson<{ ok: boolean; updated_fields: string[] }>(`${BASE}/firma/${accountId}/stammdaten`, data),

  /** Firmen-Logo aktualisieren (Base64) */
  updateLogo: (accountId: string, imageBase64: string | null) =>
    putJson<{ ok: boolean }>(`${BASE}/firma/${accountId}/logo`, { image: imageBase64 }),

  /** Neuen Ansprechpartner anlegen */
  createAnsprechpartner: (accountId: string, data: Record<string, unknown>) =>
    postJson<{ ok: boolean; kontakt_id: string }>(`${BASE}/firma/${accountId}/ansprechpartner`, data),

  /** Ansprechpartner löschen */
  deleteAnsprechpartner: (accountId: string, apId: string) =>
    deleteJson<{ ok: boolean }>(`${BASE}/firma/${accountId}/ansprechpartner/${apId}`),

  updatePerson: (accountId: string, personenId: number, data: Record<string, unknown>) =>
    putJson<unknown>(`${BASE}/firma/${accountId}/teilnehmer/${personenId}/person`, data),

  updateAnschrift: (accountId: string, personenId: number, anschriftenId: number, data: Record<string, unknown>) =>
    putJson<unknown>(`${BASE}/firma/${accountId}/teilnehmer/${personenId}/anschriften/${anschriftenId}`, data),

  updateKommunikation: (accountId: string, personenId: number, kommunikationsId: number, data: Record<string, unknown>) =>
    putJson<unknown>(`${BASE}/firma/${accountId}/teilnehmer/${personenId}/kommunikation/${kommunikationsId}`, data),

  updateZusatzstudent: (accountId: string, personenId: number, zusatzId: number, data: Record<string, unknown>) =>
    putJson<unknown>(`${BASE}/firma/${accountId}/teilnehmer/${personenId}/zusatzstudenten/${zusatzId}`, data),

  updateZusatzdaten: (accountId: string, personenId: number, zusatzdatenId: number, data: Record<string, unknown>) =>
    putJson<unknown>(`${BASE}/firma/${accountId}/teilnehmer/${personenId}/zusatzdaten/${zusatzdatenId}`, data),

  updateZahlung: (accountId: string, personenId: number, zahlungId: number, data: Record<string, unknown>) =>
    putJson<unknown>(`${BASE}/firma/${accountId}/teilnehmer/${personenId}/zahlungen/${zahlungId}`, data),

  // ── Teilnehmer anlegen ────────────────────────────────────

  /** Dublettenprüfung vor Anlage */
  dublettenCheck: (accountId: string, data: { nachname: string; vorname: string; geburtsdatum?: string; geburtsname?: string; email?: string }) =>
    postJson<DublettenErgebnis>(`${BASE}/firma/${accountId}/teilnehmer/dubletten-check`, data),

  /** Neuen Teilnehmer komplett anlegen */
  createTeilnehmer: (accountId: string, data: TeilnehmerAnlegenPayload) =>
    postJson<Record<string, unknown>>(`${BASE}/firma/${accountId}/teilnehmer/anlegen`, data),

  /** Events aus Dynamics 365 */
  events: (top = 20) =>
    fetchJson<{ events: any[]; count: number }>(`${BASE}/events?top=${top}`),

  // ── Auth ──────────────────────────────────────────────────

  authLogin: (slug: string, email: string, password: string) =>
    postJson<LoginResponse>(`${BASE}/auth/login`, { slug, email, password }),

  authRefresh: () =>
    postJson<LoginResponse>(`${BASE}/auth/refresh`, {}),

  authLogout: () =>
    postJson<unknown>(`${BASE}/auth/logout`, {}),

  authMe: () =>
    fetchJson<AuthMeResponse>(`${BASE}/auth/me`),

  /** Firma-Info per D365 Account-ID (öffentlich, für Firma-Login) */
  firmaByAccount: (accountId: string) =>
    fetchJson<{ name: string; logo: string | null }>(`${BASE}/auth/firma-by-account/${encodeURIComponent(accountId)}`),

  // ── Anmeldeportal (Superadmin) ────────────────────────────

  /** Employer-Anmeldungen auflisten */
  anmeldungenList: (params?: { skip?: number; limit?: number; semester_start?: string; hochschulort_id?: number; produkt_instanz_id?: number }) => {
    const q = new URLSearchParams()
    if (params?.skip) q.set('skip', String(params.skip))
    if (params?.limit) q.set('limit', String(params.limit))
    if (params?.semester_start) q.set('semester_start', params.semester_start)
    if (params?.hochschulort_id) q.set('hochschulort_id', String(params.hochschulort_id))
    if (params?.produkt_instanz_id) q.set('produkt_instanz_id', String(params.produkt_instanz_id))
    const qs = q.toString()
    return fetchJson<AnmeldungListResponse>(`${BASE}/anmeldungen/employer${qs ? `?${qs}` : ''}`)
  },

  /** Employer-Anmeldungen suchen */
  anmeldungenSearch: (params: { nachname?: string; vorname?: string; employer_name?: string; skip?: number; limit?: number }) => {
    const q = new URLSearchParams()
    if (params.nachname) q.set('nachname', params.nachname)
    if (params.vorname) q.set('vorname', params.vorname)
    if (params.employer_name) q.set('employer_name', params.employer_name)
    if (params.skip) q.set('skip', String(params.skip))
    if (params.limit) q.set('limit', String(params.limit))
    return fetchJson<AnmeldungListResponse>(`${BASE}/anmeldungen/employer/search?${q.toString()}`)
  },

  /** Einzelne Employer-Anmeldung abrufen */
  anmeldungDetail: (id: number) =>
    fetchJson<EmployerAnmeldungDetail>(`${BASE}/anmeldungen/employer/${id}`),

  // ── News (MyNewsdesk) ────────────────────────────────────

  /** FOM-Pressemitteilungen */
  news: (limit = 3, type = 'pressrelease') =>
    fetchJson<{ articles: NewsArticle[] }>(`${BASE}/news?limit=${limit}&type=${type}`),

  // ── Firma Permissions ────────────────────────────────────

  /** Eigene Rolle + Permissions für eine Firma (aktuell eingeloggter User) */
  getFirmaMe: (accountId: string) =>
    fetchJson<FirmaMe>(`${BASE}/firma/${accountId}/me`),

  /** Alle User einer Firma mit deren Firma-Rollen auflisten */
  getFirmaUsers: (accountId: string) =>
    fetchJson<{ users: FirmaUserSummary[] }>(`${BASE}/firma/${accountId}/users`),

  /** Firma-Rolle eines Users setzen */
  setFirmaUserRole: (accountId: string, userId: number, role: FirmaRole) =>
    putJson<{ ok: boolean }>(`${BASE}/firma/${accountId}/users/${userId}/role`, { role }),

  /** Individuelle Permissions eines Users für eine Firma lesen */
  getFirmaUserPermissions: (accountId: string, userId: number) =>
    fetchJson<{ userId: number; firmaId: number; permissions: Record<string, AccessLevel> }>(`${BASE}/firma/${accountId}/users/${userId}/permissions`),

  /** Individuelle Permissions eines Users für eine Firma setzen */
  setFirmaUserPermissions: (accountId: string, userId: number, perms: Record<string, AccessLevel>) =>
    putJson<{ ok: boolean }>(`${BASE}/firma/${accountId}/users/${userId}/permissions`, perms),

  // ── Studi-Freigabe ───────────────────────────────────────

  /** Freigabe für einen Studierenden anfordern (auth-pflichtig) */
  freigabeAnfordern: (accountId: string, personenId: number) =>
    postJson<{ ok: boolean; token?: string }>(`${BASE}/firma/${accountId}/studi/${personenId}/freigabe-anfordern`, {}),

  /** Freigabe-Token-Details abrufen (öffentlich) */
  freigabeGet: (token: string) =>
    fetchJson<{ token: string; firma_name: string; studi_name: string; expires_at: string }>(`${BASE}/freigabe/${token}`),

  /** Freigabe per Token bestätigen (öffentlich) */
  freigabeConfirm: (token: string) =>
    postJson<{ ok: boolean }>(`${BASE}/freigabe/${token}/confirm`, {}),

  // ── Superadmin ───────────────────────────────────────────
  superadmin: {
    users: (q?: string) => {
      const params = new URLSearchParams()
      if (q) params.set('q', q)
      const qs = params.toString()
      return fetchJson<{ users: UserFlatApi[] }>(`${BASE}/superadmin/users${qs ? '?' + qs : ''}`)
    },
    audit: (filters: AuditFiltersApi) => {
      const params = buildAuditParams(filters)
      return fetchJson<AuditResponseApi>(`${BASE}/superadmin/audit?${params.toString()}`)
    },
    auditActions: () => fetchJson<{ actions: string[] }>(`${BASE}/superadmin/audit/actions`),
    auditCsvUrl: (filters: AuditFiltersApi) => {
      const params = buildAuditParams(filters)
      return `${BASE}/superadmin/audit.csv?${params.toString()}`
    },
    updateUserRole: (userId: number, role: 'superadmin' | 'admin' | 'user') =>
      putJson<{ id: number; role: string }>(`${BASE}/superadmin/users/${userId}/role`, { role }),
    addSuperadmin: (body: { email: string; displayName?: string }) =>
      postJson<{ id: number; action: 'created' | 'promoted' | 'already' }>(`${BASE}/superadmin/users`, body),
    deleteUser: async (userId: number): Promise<{
      ok: boolean
      deleted: { portal_user: boolean; totp: boolean; crm_contact: boolean; crm_error: string | null }
    }> => {
      const res = await fetch(`${BASE}/superadmin/users/${userId}`, { method: 'DELETE' })
      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText)
        throw new ApiError(res.status, `API-Fehler ${res.status}: ${text}`)
      }
      return res.json()
    },
  },
}

export interface NewsArticle {
  id: string
  title: string
  summary: string
  image: string | null
  url: string
  published_at: string | null
}
